import { DefaultError, useMutation, UseMutationOptions } from '@tanstack/react-query';
import { ShopperBasketsTypes } from 'commerce-sdk-isomorphic';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { Basket } from '~/lib';
import { PaymentMethod } from '~/shared/constants';

type PaymentInstrument = ShopperBasketsTypes.OrderPaymentInstrument;

type MutationVariables = { paymentMethod: PaymentMethod };
type MutationData = ShopperBasketsTypes.Basket | undefined;
type MutationOptions = Omit<
    UseMutationOptions<MutationData, DefaultError, MutationVariables>,
    'mutationFn'
>;

export const useSetPaymentInstrument = (options?: MutationOptions) => {
    const {
        basketId,
        c_remainingTotal: storedBasketTotal = 0,
        paymentInstruments: storedPaymentInstruments,
        getLiveBasket,
        addPaymentMethodToBasket,
        updatePaymentInstrumentById,
        removePaymentInstrumentById,
    } = useBasket();

    const { mutateAsync: setPaymentInstrument, ...rest } = useMutation<
        MutationData,
        DefaultError,
        MutationVariables
    >({
        ...options,
        // This will make sure that all `setPaymentInstrument` (mutateAsync) will run in series preventing race conditions.
        scope: { id: `basket-${basketId}-setPaymentInstrument` },
        retry: 0,
        mutationFn: async ({ paymentMethod }: MutationVariables) => {
            // Always fetch live basket to make sure we have up-to-date data.
            const liveBasket = await getLiveBasket();

            const paymentInstruments =
                liveBasket?.paymentInstruments ?? storedPaymentInstruments ?? [];
            const basketTotal = liveBasket?.c_remainingTotal ?? storedBasketTotal;

            const getInstrumentsByMethod = (method: PaymentMethod) =>
                paymentInstruments.filter(
                    (
                        instrument,
                    ): instrument is PaymentInstrument & { paymentInstrumentId: string } =>
                        instrument.paymentMethodId === method && !!instrument.paymentInstrumentId,
                );

            // We will use the live basket and track changes on it as we mutate it along the way.
            let latestBasket = liveBasket;

            const removePaymentInstrumentsFromBasket = async (instruments: PaymentInstrument[]) => {
                for (const instrumentToRemove of instruments) {
                    if (!instrumentToRemove.paymentInstrumentId) continue;

                    latestBasket = (await removePaymentInstrumentById(
                        instrumentToRemove.paymentInstrumentId,
                    )) as Basket;
                }
            };

            const altaPayInstruments = getInstrumentsByMethod(PaymentMethod.ALTAPAY);
            const netsInstruments = getInstrumentsByMethod(PaymentMethod.NETS);

            switch (paymentMethod) {
                case PaymentMethod.NETS: {
                    /**
                     * We will remove existing payment instruments (i.e., ALTAPAY) when using Nets.
                     * Nets payment instrument is not needed because it will be added during order creation.
                     */
                    await removePaymentInstrumentsFromBasket(altaPayInstruments);

                    return latestBasket;
                }

                case PaymentMethod.ALTAPAY: {
                    const [altaPayInstrument, ...possibleDuplicateInstruments] = altaPayInstruments;

                    /**
                     * If a Nets payment method exists on the basket, we'll remove it.
                     * We also ocassionally encounter duplicate AltaPay instruments that we should try to prune out.
                     */
                    await removePaymentInstrumentsFromBasket([
                        ...netsInstruments,
                        ...possibleDuplicateInstruments,
                    ]);

                    /**
                     * If the basket total is 0 and we have AltaPay, we will remove it as there's no need for payment.
                     * This can be achieved with gift certificates, rewards, etc.
                     */
                    if (basketTotal <= 0) {
                        if (altaPayInstrument) {
                            await removePaymentInstrumentsFromBasket([altaPayInstrument]);
                        }

                        return latestBasket;
                    }

                    // If there's no AltaPay instrument yet, we will add it.
                    if (!altaPayInstrument) {
                        return await addPaymentMethodToBasket(PaymentMethod.ALTAPAY, {
                            amount: basketTotal,
                        });
                    }

                    // If the amount is the same as before, we'll keep it as is.
                    else if (altaPayInstrument.amount === basketTotal) return latestBasket;

                    return await updatePaymentInstrumentById(
                        altaPayInstrument.paymentInstrumentId,
                        { amount: basketTotal },
                    );
                }

                default:
                    return latestBasket;
            }
        },
    });

    return {
        setPaymentInstrument,
        ...rest,
    };
};
